import React, { useState } from 'react'

interface DatabaseTestResult {
    path: string
    success: boolean
    error?: string
    tables: string[]
    columns: string[]
    sampleData: any[]
    fieldAnalysis: {
        questionFields: string[]
        statementFields: string[]
        answerFields: string[]
        solutionFields: string[]
        imageFields: string[]
        otherFields: string[]
    }
    dsSpecificData?: {
        question_lead: any
        statements: { [key: string]: any }
        answers: { [key: string]: any }
        solutions: { [key: string]: any }
    }
}

const BankDataFieldsTest: React.FC = () => {
    const [testResults, setTestResults] = useState<DatabaseTestResult[]>([])
    const [loading, setLoading] = useState(false)

    const testAllBankDatabases = async () => {
        setLoading(true)
        setTestResults([])

        // Test all available database paths
        const testPaths = [
            '/bank/L11/C1/B01/DS/questions.db',
            '/bank/L11/C1/B01/TN/questions.db',
            '/bank/L11/C1/B01/TL/questions.db',
            '/bank/L11/C1/B04/DS/questions.db',
            '/bank/L11/C1/B04/TN/questions.db',
            '/bank/L11/C1/B04/TL/questions.db',
            '/bank/L11/C2/B07/DS/questions.db',
            '/bank/L11/C2/B07/TN/questions.db',
            '/bank/L11/C2/B07/TL/questions.db',
            '/bank/L11/C4/B15/DS/questions.db',
            '/bank/L11/C4/B15/TN/questions.db',
            '/bank/L11/C4/B15/TL/questions.db'
        ]

        const results: DatabaseTestResult[] = []

        for (const dbPath of testPaths) {
            try {
                const result = await testSingleDatabase(dbPath)
                results.push(result)
            } catch (error: any) {
                results.push({
                    path: dbPath,
                    success: false,
                    error: error.message,
                    tables: [],
                    columns: [],
                    sampleData: [],
                    fieldAnalysis: {
                        questionFields: [],
                        statementFields: [],
                        answerFields: [],
                        solutionFields: [],
                        imageFields: [],
                        otherFields: []
                    }
                })
            }
        }

        setTestResults(results)
        setLoading(false)
    }

    const testSingleDatabase = async (dbPath: string): Promise<DatabaseTestResult> => {
        console.log(`Testing database: ${dbPath}`)

        const response = await fetch(dbPath)
        if (!response.ok) {
            throw new Error(`Cannot load database: ${response.status} ${response.statusText}`)
        }

        // Load SQL.js
        const initSqlJs = (await import('sql.js')).default
        const SQL = await initSqlJs({
            locateFile: (file: string) => `https://sql.js.org/dist/${file}`
        })

        const arrayBuffer = await response.arrayBuffer()
        const db = new SQL.Database(new Uint8Array(arrayBuffer))

        try {
            // Get tables
            const tablesResult = db.exec("SELECT name FROM sqlite_master WHERE type='table'")
            const tableNames = tablesResult.length > 0 ? tablesResult[0].values.map(row => row[0] as string) : []

            if (tableNames.length === 0) {
                throw new Error('No tables found in database')
            }

            // Get data from first table
            const tableName = tableNames[0]
            const dataResult = db.exec(`SELECT * FROM ${tableName} LIMIT 3`)

            if (dataResult.length === 0 || dataResult[0].values.length === 0) {
                throw new Error(`No data found in table ${tableName}`)
            }

            const columns = dataResult[0].columns
            const rows = dataResult[0].values

            // Convert rows to objects
            const sampleData = rows.map(row => {
                const obj: any = {}
                columns.forEach((col, index) => {
                    obj[col] = row[index]
                })
                return obj
            })

            // Analyze fields
            const fieldAnalysis = analyzeFields(columns)

            // Extract DS-specific data if this is a DS database
            let dsSpecificData = undefined
            if (dbPath.includes('/DS/')) {
                dsSpecificData = extractDSData(sampleData[0])
            }

            return {
                path: dbPath,
                success: true,
                tables: tableNames,
                columns: columns,
                sampleData: sampleData,
                fieldAnalysis: fieldAnalysis,
                dsSpecificData: dsSpecificData
            }

        } finally {
            db.close()
        }
    }

    const analyzeFields = (columns: string[]) => {
        const analysis = {
            questionFields: [] as string[],
            statementFields: [] as string[],
            answerFields: [] as string[],
            solutionFields: [] as string[],
            imageFields: [] as string[],
            otherFields: [] as string[]
        }

        columns.forEach(col => {
            const lowerCol = col.toLowerCase()

            if (lowerCol.includes('question') || lowerCol.includes('cau_hoi') || lowerCol.includes('lead')) {
                analysis.questionFields.push(col)
            } else if (lowerCol.includes('statement') || lowerCol.includes('phat_bieu') || lowerCol.includes('option')) {
                analysis.statementFields.push(col)
            } else if (lowerCol.includes('answer') || lowerCol.includes('dap_an') || lowerCol.includes('correct')) {
                analysis.answerFields.push(col)
            } else if (lowerCol.includes('solution') || lowerCol.includes('giai_thich') || lowerCol.includes('explanation')) {
                analysis.solutionFields.push(col)
            } else if (lowerCol.includes('image') || lowerCol.includes('hinh_anh') || lowerCol.includes('img')) {
                analysis.imageFields.push(col)
            } else {
                analysis.otherFields.push(col)
            }
        })

        return analysis
    }

    const extractDSData = (sampleRecord: any) => {
        return {
            question_lead: sampleRecord.question_lead,
            statements: {
                statement_a: sampleRecord.statement_a,
                statement_b: sampleRecord.statement_b,
                statement_c: sampleRecord.statement_c,
                statement_d: sampleRecord.statement_d
            },
            answers: {
                answer_a: sampleRecord.answer_a,
                answer_b: sampleRecord.answer_b,
                answer_c: sampleRecord.answer_c,
                answer_d: sampleRecord.answer_d
            },
            solutions: {
                solution_a: sampleRecord.solution_a,
                solution_b: sampleRecord.solution_b,
                solution_c: sampleRecord.solution_c,
                solution_d: sampleRecord.solution_d
            }
        }
    }

    const getQuestionType = (path: string) => {
        if (path.includes('/DS/')) return 'DS (Đúng/Sai)'
        if (path.includes('/TN/')) return 'TN (Trắc nghiệm)'
        if (path.includes('/TL/')) return 'TL (Tự luận)'
        return 'Unknown'
    }

    const getPathInfo = (path: string) => {
        const match = path.match(/\/bank\/L(\d+)\/C(\d+)\/B(\d+)\/(\w+)\//)
        if (match) {
            return {
                grade: `Lớp ${match[1]}`,
                chapter: `Chương ${match[2]}`,
                lesson: `Bài ${match[3]}`,
                type: match[4]
            }
        }
        return null
    }

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <h1 className="text-3xl font-bold mb-6">Bank Data Fields Test</h1>
            <p className="text-gray-600 mb-6">
                Test tất cả các trường dữ liệu từ database trong thư mục public/bank
            </p>

            <button
                onClick={testAllBankDatabases}
                disabled={loading}
                className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 mb-6"
            >
                {loading ? 'Testing All Databases...' : 'Test All Bank Databases'}
            </button>

            {loading && (
                <div className="text-center py-8">
                    <div className="text-lg">Đang test tất cả databases...</div>
                    <div className="text-sm text-gray-500 mt-2">Có thể mất vài phút</div>
                </div>
            )}

            <div className="space-y-6">
                {testResults.map((result, index) => {
                    const pathInfo = getPathInfo(result.path)
                    const questionType = getQuestionType(result.path)

                    return (
                        <div key={index} className="border border-gray-300 rounded-lg p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h2 className="text-xl font-semibold">
                                        {pathInfo ? `${pathInfo.grade} - ${pathInfo.chapter} - ${pathInfo.lesson}` : 'Unknown'}
                                    </h2>
                                    <div className="text-sm text-gray-600">
                                        {questionType} | {result.path}
                                    </div>
                                </div>
                                <div className={`px-3 py-1 rounded text-sm font-medium ${result.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                    }`}>
                                    {result.success ? '✅ Success' : '❌ Failed'}
                                </div>
                            </div>

                            {result.error && (
                                <div className="text-red-600 mb-4 p-3 bg-red-50 rounded">
                                    <strong>Error:</strong> {result.error}
                                </div>
                            )}

                            {result.success && (
                                <>
                                    {/* Basic Info */}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                        <div className="bg-gray-50 p-3 rounded">
                                            <div className="text-sm font-medium">Tables</div>
                                            <div className="text-sm text-gray-600">{result.tables.join(', ')}</div>
                                        </div>
                                        <div className="bg-gray-50 p-3 rounded">
                                            <div className="text-sm font-medium">Columns</div>
                                            <div className="text-sm text-gray-600">{result.columns.length} fields</div>
                                        </div>
                                        <div className="bg-gray-50 p-3 rounded">
                                            <div className="text-sm font-medium">Sample Records</div>
                                            <div className="text-sm text-gray-600">{result.sampleData.length} records</div>
                                        </div>
                                    </div>

                                    {/* Field Analysis */}
                                    <div className="mb-6">
                                        <h3 className="text-lg font-medium mb-3">Field Analysis</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {Object.entries(result.fieldAnalysis).map(([category, fields]) => (
                                                <div key={category} className="bg-gray-50 p-3 rounded">
                                                    <div className="text-sm font-medium capitalize mb-2">
                                                        {category.replace('Fields', '')} ({fields.length})
                                                    </div>
                                                    <div className="text-xs space-y-1">
                                                        {fields.length > 0 ? (
                                                            fields.map(field => (
                                                                <div key={field} className="text-gray-600">• {field}</div>
                                                            ))
                                                        ) : (
                                                            <div className="text-gray-400">None found</div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* DS-Specific Data */}
                                    {result.dsSpecificData && (
                                        <div className="mb-6">
                                            <h3 className="text-lg font-medium mb-3">DS-Specific Data</h3>
                                            <div className="space-y-4">
                                                <div className="bg-blue-50 p-4 rounded">
                                                    <div className="font-medium mb-2">Question Lead:</div>
                                                    <div className="text-sm text-gray-700 break-words">
                                                        {result.dsSpecificData.question_lead || '[NULL]'}
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                    <div className="bg-green-50 p-4 rounded">
                                                        <div className="font-medium mb-2">Statements:</div>
                                                        <div className="text-xs space-y-1">
                                                            {Object.entries(result.dsSpecificData.statements).map(([key, value]) => (
                                                                <div key={key}>
                                                                    <strong>{key.replace('statement_', '').toUpperCase()}:</strong> {
                                                                        value ? (String(value).length > 50 ? String(value).substring(0, 50) + '...' : String(value)) : '[NULL]'
                                                                    }
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div className="bg-orange-50 p-4 rounded">
                                                        <div className="font-medium mb-2">Answers:</div>
                                                        <div className="text-xs space-y-1">
                                                            {Object.entries(result.dsSpecificData.answers).map(([key, value]) => (
                                                                <div key={key}>
                                                                    <strong>{key.replace('answer_', '').toUpperCase()}:</strong> {value || '[NULL]'}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div className="bg-purple-50 p-4 rounded">
                                                        <div className="font-medium mb-2">Solutions:</div>
                                                        <div className="text-xs space-y-1">
                                                            {Object.entries(result.dsSpecificData.solutions).map(([key, value]) => (
                                                                <div key={key}>
                                                                    <strong>{key.replace('solution_', '').toUpperCase()}:</strong> {
                                                                        value ? 'Available' : '[NULL]'
                                                                    }
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Sample Data */}
                                    <div>
                                        <h3 className="text-lg font-medium mb-3">Sample Data (First Record)</h3>
                                        <div className="bg-gray-50 p-4 rounded max-h-96 overflow-y-auto">
                                            <div className="space-y-2 text-sm">
                                                {Object.entries(result.sampleData[0] || {}).map(([key, value]) => (
                                                    <div key={key} className="flex">
                                                        <div className="w-1/4 font-medium text-gray-700">{key}:</div>
                                                        <div className="w-3/4 text-gray-600 break-words">
                                                            {value === null || value === undefined ? '[NULL]' :
                                                                typeof value === 'string' && value.length > 100 ?
                                                                    value.substring(0, 100) + '...' : String(value)}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )
                })}
            </div>

            {testResults.length > 0 && (
                <div className="mt-8 p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-medium mb-2">Summary</h3>
                    <div className="text-sm space-y-1">
                        <div>Total Databases Tested: {testResults.length}</div>
                        <div>Successful: {testResults.filter(r => r.success).length}</div>
                        <div>Failed: {testResults.filter(r => !r.success).length}</div>
                        <div>DS Databases: {testResults.filter(r => r.path.includes('/DS/')).length}</div>
                        <div>TN Databases: {testResults.filter(r => r.path.includes('/TN/')).length}</div>
                        <div>TL Databases: {testResults.filter(r => r.path.includes('/TL/')).length}</div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default BankDataFieldsTest